import moment from 'moment';
import { FC, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Spinner } from '@common/components/Spinner';
import {
  useGetSpaPackage,
  useGetSpaPackageConfigGroups,
  useGetSpaPackageTimeslots,
} from '@features/spa';
import { useGetSpaSettings } from '@features/spa/hooks';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { Property } from 'shared-types/Property';
import { SpaPackageType, TimeSlotType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../SpaPackageForm.schema';
import { calculateExtraPrices } from '../SpaPackageModal/SpaPackageModal.helpers';
import { ExtrasFormDataType } from '../SpaPackageModal/SpaPackageModal.types';

import { SpaDaysBookingForm } from 'voyager';

import { calculateTotalPrice } from './SpaDayBooking.helpers';
import { useConfigGroupHandlers } from './hooks/useConfigGroupHandlers';
import { useDateManagement } from './hooks/useDateManagement';
import { useGuestConfig } from './hooks/useGuestConfig';
import { usePreferences } from './hooks/usePreferences';
import { useSubmitBooking } from './hooks/useSubmitBooking';

type SpaDayBookingProps = {
  id: number;
  property: Property;
  language: string;
  startDate: string;
  onClose: (showMiniBasket: boolean) => void;
};

export const SpaDayBooking: FC<SpaDayBookingProps> = ({ id, property, language, onClose }) => {
  const { t } = useTranslations();
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );

  const form = useFormContext<SpaPackageFormValues>();

  const { data: spaPackage, isLoading: isLoadingPackage } = useGetSpaPackage(
    property.id,
    id,
    language
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;

  const entity = spaPackage as SpaPackageType | undefined;

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedExtras = useWatch({ control: form.control, name: 'extras' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  const unavailable = false;

  const [weekStartDate, setWeekStartDate] = useState<string>(() => {
    const d = moment(selectedDateString);
    return d.clone().startOf('isoWeek').format('YYYY-MM-DD');
  });

  const { data: timeslots, isLoading: isLoadingTimeslots } = useGetSpaPackageTimeslots(
    property.id,
    id,
    weekStartDate,
    entity?.numberOfGuests,
    7,
    !unavailable
  );

  const { availability, handleDateSelect, handleWeekNavigate } = useDateManagement({
    form,
    selectedDateString,
    weekStartDate,
    setWeekStartDate,
    timeslots,
  });

  const selectedTime = watchedArrivalTime?.startTime;
  const selectedDateTimeSlots = timeslots?.[selectedDateString];

  const endTime = selectedDateTimeSlots?.find(
    (slot: TimeSlotType) => slot.startTime === selectedTime
  )?.endTime;

  const { data: configurationGroups = [], isLoading: isLoadingConfigGroups } =
    useGetSpaPackageConfigGroups(
      entity?.configurationGroups || [],
      property.id,
      id,
      selectedDateString,
      watchedGuests?.length || 1,
      selectedTime,
      endTime
    );

  const isLoading = isLoadingPackage || isLoadingTimeslots || isLoadingConfigGroups;

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    id,
    language,
    selectedDateString
  );

  const price = selectedDateTimeSlots?.find((slot) => slot.startTime === selectedTime)?.price;

  const guestsCount = watchedGuests?.length || 0;
  const extrasTotal = calculateExtraPrices(
    Object.fromEntries(
      Object.entries(watchedExtras || {}).filter(([, v]) => Array.isArray(v))
    ) as ExtrasFormDataType
  );

  const displayedPrice = price || entity?.price;
  const totalPrice = displayedPrice
    ? calculateTotalPrice(displayedPrice, guestsCount, extrasTotal)
    : null;

  const configGroupsNoExtras = useMemo(
    () => configurationGroups.filter((cg) => !cg.isExtras),
    [configurationGroups]
  );

  const {
    titleOptions,
    genderOpts,
    guests,
    handleGuestCountChange,
    handleGuestChange,
    handleExistingGuestSelect,
  } = useGuestConfig({
    form,
    watchedGuests,
    configGroupsNoExtras,
    showGenderNeutral,
  });

  const {
    handleAllOptionsTimeslotChange,
    handleSingleOptionSelect,
    handleSingleOptionTimeslotChange,
  } = useConfigGroupHandlers({ form, configGroupsNoExtras });

  const { notesEnabled, handlePreferencesToggle, handleFreeTextChange, preferenceCategories } =
    usePreferences({ form });

  const hasExtras = extras.length > 0;

  const { handleSubmit, handleNextStep, handleBack } = useSubmitBooking({
    form,
    entity,
    propertyId: property.id,
    language,
    id,
    onClose,
  });

  if (!entity) return null;

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <form onSubmit={handleSubmit} autoComplete="on">
        <SpaDaysBookingForm
          imageUrl={entity.image?.url}
          imageAlt={entity.name}
          onBack={handleBack}
          title={entity.name}
          isLoading={isLoading}
          guestCount={guestsCount}
          minGuests={entity.numberOfGuests}
          maxGuests={spaPackage?.maximumGuestsPerBasket || undefined}
          onGuestCountChange={handleGuestCountChange}
          activeDate={weekStartDate}
          selectedDate={selectedDateString}
          availability={availability}
          onDateSelect={handleDateSelect}
          onWeekNavigate={handleWeekNavigate}
          guests={guests}
          onExistingGuestSelect={handleExistingGuestSelect}
          onGuestChange={handleGuestChange}
          titleOptions={titleOptions}
          genderOptions={genderOpts}
          onAllOptionsTimeslotChange={handleAllOptionsTimeslotChange}
          onSingleOptionSelect={handleSingleOptionSelect}
          onSingleOptionTimeslotChange={handleSingleOptionTimeslotChange}
          preferencesEnabled={notesEnabled}
          onPreferencesToggle={handlePreferencesToggle}
          preferenceCategories={preferenceCategories}
          expandedPreferences={notesEnabled ? ['notes'] : []}
          onPreferenceCategoryToggle={() => {}}
          onPreferenceOptionToggle={() => {}}
          onPreferenceOtherChange={() => {}}
          onPreferenceFreeTextChange={handleFreeTextChange}
          stickyFooter={
            !entity.telephoneOnly && totalPrice
              ? {
                  title: entity.name,
                  totalAmount: totalPrice.amount,
                  currencyCode: totalPrice.currencyCode,
                  totalLabel: t('basket.total'),
                  ctaLabel: hasExtras ? t('spa.package.nextStep') : t('product.ctaButton'),
                  onCtaClick: hasExtras ? handleNextStep : handleSubmit,
                  ctaDisabled: unavailable || !form.formState.isValid,
                }
              : undefined
          }
        />
      </form>
    </>
  );
};
